import { useState, useCallback, useRef, useEffect } from "react";

interface UseSpeechSynthesisOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  voiceName?: string;
  onEnd?: () => void;
  onError?: (error: string) => void;
}

export function useSpeechSynthesis({
  rate = 1,
  pitch = 1,
  volume = 1,
  voiceName,
  onEnd,
  onError,
}: UseSpeechSynthesisOptions = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const onEndRef = useRef(onEnd);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onEndRef.current = onEnd;
    onErrorRef.current = onError;
  }, [onEnd, onError]);

  useEffect(() => {
    const supported = "speechSynthesis" in window;
    setIsSupported(supported);
    console.log("[SpeechSynthesis] Supported:", supported);

    if (!supported) return;

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      console.log("[SpeechSynthesis] Voices loaded:", availableVoices.length);
      setVoices(availableVoices);
    };

    // Load voices immediately and on change
    loadVoices();
    
    // Chrome/Safari load voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    // Fallback for Safari - poll for voices
    if (window.speechSynthesis.getVoices().length === 0) {
      const pollVoices = setInterval(() => {
        const v = window.speechSynthesis.getVoices();
        if (v.length > 0) {
          setVoices(v);
          clearInterval(pollVoices);
        }
      }, 100);
      
      // Clean up after 3 seconds
      setTimeout(() => clearInterval(pollVoices), 3000);
    }

    return () => {
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speak = useCallback(
    (text: string) => {
      if (!("speechSynthesis" in window) || !text) {
        console.log("[SpeechSynthesis] Cannot speak:", { hasAPI: "speechSynthesis" in window, hasText: !!text });
        return;
      }

      console.log("[SpeechSynthesis] Speaking:", text.substring(0, 50) + "...");

      // Cancel any ongoing speech first
      window.speechSynthesis.cancel();
      
      // iOS Safari fix: Need to resume if paused
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = volume;

      // Get voices fresh each time for iOS
      const currentVoices = window.speechSynthesis.getVoices();
      console.log("[SpeechSynthesis] Available voices:", currentVoices.length);
      
      let selectedVoice: SpeechSynthesisVoice | undefined;
      
      if (voiceName) {
        selectedVoice = currentVoices.find((v) => v.name === voiceName);
      }
      
      if (!selectedVoice && currentVoices.length > 0) {
        // Priority order for natural-sounding voices
        selectedVoice = 
          currentVoices.find((v) => v.name.includes("Samantha")) || // iOS default
          currentVoices.find((v) => v.name.includes("Karen")) || // iOS Australian
          currentVoices.find((v) => v.name.includes("Daniel")) || // iOS British
          currentVoices.find((v) => v.name.includes("Natural") && v.lang.startsWith("en")) ||
          currentVoices.find((v) => v.name.includes("Google") && v.lang.startsWith("en")) ||
          currentVoices.find((v) => v.lang.startsWith("en-US")) ||
          currentVoices.find((v) => v.lang.startsWith("en")) ||
          currentVoices[0]; // Fallback to first available
      }

      if (selectedVoice) {
        console.log("[SpeechSynthesis] Using voice:", selectedVoice.name);
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
      } else {
        utterance.lang = "en-US";
      }

      utterance.onstart = () => {
        console.log("[SpeechSynthesis] ✓ Started speaking");
        setIsSpeaking(true);
      };

      utterance.onend = () => {
        console.log("[SpeechSynthesis] ✓ Finished speaking");
        setIsSpeaking(false);
        onEndRef.current?.();
      };

      utterance.onerror = (event) => {
        console.error("[SpeechSynthesis] Error:", event.error);
        setIsSpeaking(false);
        // Don't report 'interrupted' as an error - happens when we cancel
        if (event.error !== 'interrupted') {
          onErrorRef.current?.(event.error);
        }
      };

      utteranceRef.current = utterance;
      
      // iOS Safari requires speak() to be called from user gesture context
      // Use a minimal delay to ensure the audio system is ready
      setTimeout(() => {
        try {
          window.speechSynthesis.speak(utterance);
          console.log("[SpeechSynthesis] speak() called");
          
          // iOS Safari bug: speaking can get stuck, check and resume
          setTimeout(() => {
            if (window.speechSynthesis.speaking && window.speechSynthesis.paused) {
              console.log("[SpeechSynthesis] Resuming stuck speech");
              window.speechSynthesis.resume();
            }
          }, 100);
        } catch (e) {
          console.error("[SpeechSynthesis] speak() failed:", e);
          setIsSpeaking(false);
        }
      }, 50);
    },
    [rate, pitch, volume, voiceName]
  );

  const stop = useCallback(() => {
    if (isSupported) {
      console.log("[SpeechSynthesis] Stopping");
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, [isSupported]);

  const pause = useCallback(() => {
    if (isSupported) {
      window.speechSynthesis.pause();
    }
  }, [isSupported]);

  const resume = useCallback(() => {
    if (isSupported) {
      window.speechSynthesis.resume();
    }
  }, [isSupported]);

  return {
    speak,
    stop,
    pause,
    resume,
    isSpeaking,
    isSupported,
    voices,
  };
}
